/** Play statistics, only with consent (docs/ANALYTICS_EVENTS.md, section B). Before the player says yes, nothing is
 * stored or sent: `track` is a no-op. After yes, a random analytics ID lives on the device and events go to our own
 * server in small batches (never to anyone else). Saying no, or withdrawing in Settings, stops it at once. */

const CONSENT_KEY = 'spermtan.rush.consent.v1', ID_KEY = 'spermtan.rush.analytics.v1', AGAIN_KEY = 'spermtan.rush.playAgain.v1', ASKED_KEY = 'spermtan.rush.consentAsked.v1';
/** When the question comes (games played, counted by "Play again"): first on the 3rd; after a "No thanks", again on the
 * 10th, 15th and 20th; then never. A choice made in Settings is final: no more asking. */
export const ASK_AT = [3, 10, 15, 20];
export type Consent = 'yes' | 'no' | null;
type Event = { name: string; at: number; props: Record<string, unknown> };

let queue: Event[] = [], sessionId: string | null = null, timer: ReturnType<typeof setInterval> | null = null, build = 'web';
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, char => (Number(char) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(char) / 4)))).toString(16)));

export function consent(): Consent { try { const value = localStorage.getItem(CONSENT_KEY); return value === 'yes' || value === 'no' ? value : null; } catch { return null; } }
export function analyticsId(): string | null { try { return consent() === 'yes' ? localStorage.getItem(ID_KEY) : null; } catch { return null; } }

/** Records the player's answer. Yes creates the analytics ID; no (or withdrawing) deletes it and anything queued. */
export function setConsent(value: 'yes' | 'no') {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    if (value === 'yes' && !localStorage.getItem(ID_KEY)) localStorage.setItem(ID_KEY, uuid());
    if (value === 'no') { localStorage.removeItem(ID_KEY); queue = []; }
  } catch { /* storage blocked: nothing is tracked */ }
  if (value === 'yes') { start(build); track('consent_given'); } else stop();
}

/** Should the question be shown now (after this many games)? */
export function shouldAskConsent(games: number): boolean {
  if (consent() === 'yes') return false;
  let asked = 0;
  try { asked = Number(localStorage.getItem(ASKED_KEY) ?? 0); } catch { return false; }
  return asked < ASK_AT.length && games >= ASK_AT[asked];
}
/** Records that the question was answered from the sheet (one step along ASK_AT), or finally from Settings. */
export function markAsked(final = false) {
  try {
    const asked = Number(localStorage.getItem(ASKED_KEY) ?? 0);
    localStorage.setItem(ASKED_KEY, String(final ? ASK_AT.length : asked + 1));
  } catch { /* storage blocked: the question simply won't repeat */ }
}

/** "Play again" presses on this device: the consent question comes on the third (see ASK_AT). */
export function countPlayAgain(): number {
  try { const next = Number(localStorage.getItem(AGAIN_KEY) ?? 0) + 1; localStorage.setItem(AGAIN_KEY, String(next)); return next; } catch { return 0; }
}

export function track(name: string, props: Record<string, unknown> = {}) {
  if (!analyticsId()) return;
  queue.push({ name, at: Date.now(), props });
  if (queue.length >= 40) void flush();
}

/** Starts a session (if consented): a batch every 15 s and on leaving the page; taps on [data-track] elements count. */
export function start(buildId: string) {
  build = buildId;
  if (!analyticsId() || timer) return;
  sessionId = uuid();
  const params = new URLSearchParams(location.search);
  track('session_start', { page: location.pathname, referrer: document.referrer ? new URL(document.referrer).hostname : null, share: params.get('r') ?? sessionStorage.getItem('spermtan.rush.ref') ?? null, screen: `${screen.width}x${screen.height}` });
  timer = setInterval(() => void flush(), 15_000);
  addEventListener('pagehide', onHide); document.addEventListener('click', onClick, true);
}
export function stop() {
  if (timer) clearInterval(timer);
  timer = null; queue = [];
  removeEventListener('pagehide', onHide); document.removeEventListener('click', onClick, true);
}
function onHide() { track('session_end', { duration: Math.round(performance.now() / 1000) }); void flush(true); }
function onClick(event: MouseEvent) {
  const element = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-track]');
  if (element) track('button_press', { id: element.dataset.track, page: location.pathname });
}

async function flush(leaving = false) {
  const id = analyticsId();
  if (!id || !sessionId || !queue.length) return;
  const batch = JSON.stringify({ analyticsId: id, sessionId, build, events: queue.splice(0, 100) });
  if (leaving && navigator.sendBeacon) { navigator.sendBeacon('/api/rush/events', new Blob([batch], { type: 'application/json' })); return; }
  try { await fetch('/api/rush/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body: batch, keepalive: true }); } catch { /* dropped */ }
}
